import {
  Button,
  ButtonCopyText,
  Environment,
  HeimdallService,
  Loader,
  LogService,
  MediaKit,
  MenuOptions,
  ToastHelper,
  defaultUser,
  onEnvironmentChange,
} from '@hub/shared'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Subscription, SubscriptionLike } from 'rxjs'
import ModalStatisticsComponent from '../modal-statistics/modal-statistics.component'
import './card-mediakit.component.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  mediakit?: MediaKit
  onChangeMediakit?: (value: any) => any
}

export default ({ mediakit, onChangeMediakit }: Props) => {
  const navigate = useNavigate()
  const [t, i18n] = useTranslation('cardMediaKit')
  const [showStatistics, setShowStatistics] = useState<boolean>(false)
  const environmentChange = useRef<SubscriptionLike | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(false)
  const heimdallService = useMemo(() => new HeimdallService(), [])
  const [preview, setPreview] = useState(!mediakit?.enabled)
  const [mediaKitUrl, setMediaKitUrl] = useState<string>('')
  const [menuActions, setMenuActions] = useState<{ label: string; icon?: string; iconText?: string; action: () => void }[]>([
    { label: t('actions.statistics'), icon: 'chart-line', action: () => setShowStatistics(true) },
    { label: t('actions.disable'), icon: 'eye-slash', action: () => onDisableMediaKit() },
  ])
  const toast = useMemo(() => new ToastHelper().toast, [])
  const logService = useMemo(() => new LogService(), [])
  logService.init('Card Media Kit')

  const onChangeMenuOptions = useCallback(() => {
    setMenuActions((menu) => {
      return menu?.map((m) => {
        let key = 'actions.disable'
        if (m.icon === 'chart-line') {
          key = 'actions.statistics'
        } else if (!mediakit?.enabled) {
          key = 'actions.enable'
          m.icon = 'eye'
        }
        return {
          ...m,
          label: t(key),
        }
      })
    })
  }, [mediakit?.enabled, t])

  useEffect(() => {
    environmentChange.current = onEnvironmentChange.pipe().subscribe((environment: Environment) => {
      setMediaKitUrl(environment?.squidProducts?.mediaKit)
    })

    return () => environmentChange?.current?.unsubscribe()
  }, [])

  useEffect(() => {
    onChangeMenuOptions()
  }, [i18n.language, onChangeMenuOptions])

  useEffect(() => {
    setPreview(!mediakit?.enabled)
  }, [mediakit?.enabled])

  const onDisableMediaKit = () => {
    setLoading(true)
    if (mediakit) {
      mediakit.enabled = !mediakit?.enabled
    }

    heimdallService
      .patchMediaKit(mediakit!)
      .then((resp) => {
        if (onChangeMediakit && typeof onChangeMediakit === 'function') {
          onChangeMediakit(true)
        }
        onChangeMenuOptions()
        toast.success(t('sucesseEnabledMediaKit'), {
          closeButton: true,
          className: 'full text-bold',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          notOverClick: true,
          immediately: true,
        })
      })
      .catch((error) => {
        logService.error({
          message: 'Failed to disabled media kit',
          error: error,
        })
        return error
      })
      .finally(() => {
        setLoading(false)
      })
  }

  let loader
  if (loading) {
    loader = (
      <div className="loading">
        <Loader />
      </div>
    )
  }

  return (
    <div className={'card-media-kit-total ' + (!mediakit?.enabled ? 'mediakit-disabled' : '')}>
      <div className="card-media-kit">
        {loader}
        <div className="menu-card-media-kit">
          <Button
            title="Edit media kit"
            onClick={() => navigate(`/mediakit/${mediakit?.profileId}`)}
            role="button"
            tabIndex={0}
            color="transparent"
            borderColor="transparent"
            textColor="var(--primary_color)"
            customPadding="0"
          >
            <i className="fa-regular fa-pen-to-square edit-icon"></i>
          </Button>
          <MenuOptions className="menu-card-media-kit" menuArray={menuActions} />
        </div>
        <div className="card-middle-media-kit">
          <div className="image-card-media-kit">
            <figure>
              <img
                onError={({ currentTarget }) => {
                  currentTarget.src = defaultUser
                }}
                src={`https://storage.googleapis.com/squid-profile-pictures/${mediakit?.profileId}.jpg`}
                alt={mediakit?.name}
                title={mediakit?.name}
                className="img-fluid"
              />
            </figure>
            <span className="social-icon background-instagram">
              <i className="fa-brands fa-instagram"></i>
            </span>
          </div>
          <div className="text-card-media-kit">
            {!mediakit?.enabled ? (
              <span className="p-1 label-disabled" style={{ backgroundColor: 'var(--background)' }}>
                <i className="fa-regular fa-eye-slash"></i> {t('hide')}
              </span>
            ) : null}
            <h4>{mediakit?.name}</h4>
            <p>{`@${mediakit?.username}`}</p>
          </div>
        </div>
        <ButtonCopyText
          className="copy-button"
          content={`${mediaKitUrl}/${mediakit?.username}${preview ? '?preview=true' : ''}`}
          maxWidth={window.innerWidth > 700 ? '350px' : '300px'}
        ></ButtonCopyText>
      </div>
      <ModalStatisticsComponent open={showStatistics} analytics={mediakit?.analytics} onOpenChange={(res) => setShowStatistics(res)} />
    </div>
  )
}
